import React, { useEffect } from 'react';
import styled from 'styled-components';
import ProjectElementList from '../component/projectDetail/ProjectElementList';
import ProjectDetailContainer from '../component/projectDetail/ProjectDetailContainer';
import { useParams } from 'react-router-dom';
import { useProjectDetailActions, useProjectDetailState } from '../shared/hooks/useApi/useProjectDetail';
import { useShortCut } from '../shared/hooks/useShortCut';
import ProjectInfoList from '../component/projectDetail/ProjectInfoList';
import ProjectInfoListMobile from '../component/projectDetail/mobile/ProjectInfoListMobile';
import ScrollDownArrow from '../shared/component/ScrollDownArrow';
import { useIsPageOwner } from '../shared/hooks/useIsPageOwner';
import MobileConvertWrapper from '../shared/component/MobileConvertWrapper';
import { usePageOwnerIdentityStore } from '../shared/store/pageOwnerIdentityStore';
import { isPageOwnerMatchedToRaw } from '../shared/util/pageOwnerMatch';
import { canShowProjectDetail } from '../shared/util/visibility/projectDetailVisibility';
import type { ResourceScope } from '../shared/util/scopedResource';
import { useAuthStore, useAuthTokenStore } from '../shared/store/authStore';

const ProjectDetail: React.FC = () => {
  const { HANDLE: raw, projectId } = useParams<{ HANDLE?: string; projectId: string }>();
  const isOwner = useIsPageOwner();
  const hasAuthToken = useAuthTokenStore((s) => Boolean(s.authToken));
  const userAui = useAuthStore((s) => s.user?.aui);
  const { project, projectId: cachedProjectId, projectOwnerAui, projectScope, status } = useProjectDetailState();
  const { getProject, getProjectOwner } = useProjectDetailActions();
  const { handleCmdE } = useShortCut();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShow = canShowProjectDetail({
    pageOwnerAui: pageOwner?.aui,
    resourceOwnerAui: projectOwnerAui,
    resourceScope: projectScope,
    currentScope,
    status,
    resourceProjectId: cachedProjectId,
    currentProjectId: projectId,
  });

  useEffect(() => {
    /**
     * ProjectDetail 조회
     * 
     * owner라면 isPublic 상관없이 조회  
     * visitor라면  
     * isPublic = true만 조회 가능  
     * isPublic = false라면 UAA 리턴되어서 '/'로 이동  
     */
    const getProjectWithApi = async () => {
      if (!projectId) return;
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;
      // 토큰은 있는데 user 미복원: owner 미확정 → private 신규 프로젝트에 public 타격 방지
      if (hasAuthToken && userAui == null) return;

      if (isOwner) {
        await getProjectOwner(projectId);
      } else {
        await getProject(projectId);
      }
    }
    getProjectWithApi();
  }, [raw, pageOwner?.aui, projectId, isOwner, hasAuthToken, userAui]);

  if (!canShow || !project) return null;

  return (
    <ProjectDetailPage
      onKeyDown={(e) => handleCmdE(e)}
      tabIndex={-1}
    >
      <ScrollDownArrow />
      <ProjectDetailContainer />
      <MobileConvertWrapper
        onlyMobileChildren={<ProjectInfoListMobile />}
        onlyNotMobileChildren={<ProjectInfoList />}
      />
      <ProjectElementList />
    </ProjectDetailPage>
  );
}

const ProjectDetailPage = styled.main`
  width: 100vw;
  height: 100%;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`


export default ProjectDetail;
